/**
 * @description Channel Partner sending-domain DNS sheet (LYN-308)
 *
 * Key features:
 * - Connect / records / Verify (start once) / 30s GET poll / disconnect on the 161 contract
 * - Read-only Default From Name (platformName only) and Default From Email
 * - Extracted so later Channel Partner admin Integrations → Email can remount
 *
 * @dependencies
 * - adminApi: get/connect/verify/disconnect email-sending-domain
 *
 * @notes
 * - Super Admin door this ticket. Do not mount Website / SO host panels
 * - Do not mix Domain-tab app-host rows. send. MX is bounce, not mailbox
 * - MX row paints Priority (Resend 10). CNAME paints Proxy status DNS Only
 * - No Resend API key. Factory Integrations → Email stays Lynx-wide
 * - Overlay is banner-only. This panel does not start switch-into
 */

import { useCallback, useEffect, useState } from 'react';
import {
  AlertCircle,
  CheckCircle2,
  Copy,
  Link2,
  Loader2,
  Mail,
  RefreshCw,
  Trash2
} from 'lucide-react';
import {
  connectChannelPartnerEmailSendingDomain,
  disconnectChannelPartnerEmailSendingDomain,
  getChannelPartnerEmailSendingDomain,
  verifyChannelPartnerEmailSendingDomain
} from '../../services/api/adminApi';

export const EMAIL_SENDING_DOMAIN_POLL_MS = 30000;

const STEPS = {
  CONNECT: 'connect',
  DNS: 'dns',
  VERIFIED: 'verified'
};

function CopyButton({ value, label }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
      aria-label={label}
    >
      {copied ? (
        <>
          <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
          Copied
        </>
      ) : (
        <>
          <Copy className="h-3.5 w-3.5" />
          Copy
        </>
      )}
    </button>
  );
}

function DnsRecordRow({ record }) {
  const showPriority = record.priority !== undefined && record.priority !== null && record.priority !== '';
  const showProxy = Boolean(record.proxyStatus);

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
          {record.type} record
        </span>
        <span className="text-xs text-gray-500">{record.label}</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
        <div>
          <p className="text-xs font-medium text-gray-500 mb-1">Host / Name</p>
          <div className="flex items-center gap-2">
            <code className="flex-1 font-mono text-gray-900 break-all">{record.hostname}</code>
            <CopyButton value={record.hostname} label="Copy hostname" />
          </div>
        </div>
        <div>
          <p className="text-xs font-medium text-gray-500 mb-1">Value</p>
          <div className="flex items-center gap-2">
            <code className="flex-1 font-mono text-gray-900 break-all">{record.value}</code>
            <CopyButton value={record.value} label="Copy value" />
          </div>
        </div>
        {showPriority && (
          <div>
            <p className="text-xs font-medium text-gray-500 mb-1">Priority</p>
            <div className="flex items-center gap-2">
              <code className="flex-1 font-mono text-gray-900 break-all">{record.priority}</code>
              <CopyButton value={String(record.priority)} label="Copy priority" />
            </div>
          </div>
        )}
        {showProxy && (
          <div>
            <p className="text-xs font-medium text-gray-500 mb-1">Proxy status</p>
            <div className="flex items-center gap-2">
              <code className="flex-1 font-mono text-gray-900 break-all">{record.proxyStatus}</code>
              <CopyButton value={record.proxyStatus} label="Copy proxy status" />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ReadOnlyField({ label, value }) {
  return (
    <div className="grid grid-cols-3 gap-4 px-5 py-3">
      <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">{label}</p>
      <p className="col-span-2 text-sm text-gray-900 font-mono break-all">{value || '—'}</p>
    </div>
  );
}

/**
 * @param {object} props
 * @param {string} props.channelPartnerId
 */
export default function ChannelPartnerEmailSendingDomainPanel({ channelPartnerId }) {
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [domainInput, setDomainInput] = useState('');
  const [connecting, setConnecting] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);

  const applyPayload = useCallback((data) => {
    setPayload(data || null);
    const hostname = data?.customDomain?.hostname;
    if (hostname) {
      setDomainInput(hostname);
    }
  }, []);

  const loadStatus = useCallback(async () => {
    const result = await getChannelPartnerEmailSendingDomain(channelPartnerId);
    applyPayload(result.data);
    return result.data;
  }, [applyPayload, channelPartnerId]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    loadStatus()
      .catch((err) => {
        if (!cancelled) {
          setError(err.response?.data?.error?.message || 'Failed to load sending domain');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [loadStatus]);

  const customDomain = payload?.customDomain;
  const status = customDomain?.status || 'none';
  const isVerified = status === 'verified';
  const isPending = status === 'pending' || status === 'failed';
  const records = customDomain?.dnsInstructions?.records || [];
  const step = isVerified ? STEPS.VERIFIED : (isPending ? STEPS.DNS : STEPS.CONNECT);

  useEffect(() => {
    if (!isPending || !channelPartnerId) {
      return undefined;
    }
    const intervalId = setInterval(() => {
      getChannelPartnerEmailSendingDomain(channelPartnerId)
        .then((result) => applyPayload(result.data))
        .catch(() => {});
    }, EMAIL_SENDING_DOMAIN_POLL_MS);
    return () => clearInterval(intervalId);
  }, [applyPayload, channelPartnerId, isPending]);

  const handleConnect = async (event) => {
    event.preventDefault();
    setConnecting(true);
    setError(null);
    try {
      const result = await connectChannelPartnerEmailSendingDomain(
        channelPartnerId,
        domainInput.trim()
      );
      applyPayload(result.data);
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Failed to connect sending domain');
    } finally {
      setConnecting(false);
    }
  };

  const handleVerify = async () => {
    setVerifying(true);
    setError(null);
    try {
      const result = await verifyChannelPartnerEmailSendingDomain(channelPartnerId);
      applyPayload(result.data);
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Failed to verify sending domain');
    } finally {
      setVerifying(false);
    }
  };

  const handleDisconnect = async () => {
    setDisconnecting(true);
    setError(null);
    try {
      const result = await disconnectChannelPartnerEmailSendingDomain(channelPartnerId);
      applyPayload(result.data);
      setDomainInput('');
      setShowDisconnectConfirm(false);
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Failed to disconnect sending domain');
    } finally {
      setDisconnecting(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl p-8 flex justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-200 rounded-xl">
      <div className="px-5 py-3 border-b border-gray-100 flex items-center gap-2">
        <Mail className="h-4 w-4 text-gray-500" />
        <div>
          <h2 className="text-sm font-medium text-gray-900">Email sending domain</h2>
          <p className="text-xs text-gray-500 mt-0.5">
            Paste the DNS sheet Resend returned. A send. MX is bounce mail, not the company inbox.
            Do not move company mail.
          </p>
        </div>
      </div>

      <div className="divide-y divide-gray-100">
        <ReadOnlyField label="Default From Name" value={payload?.fromName} />
        <ReadOnlyField label="Default From Email" value={payload?.fromEmail} />
      </div>

      <div className="px-5 py-4 space-y-4">
        {error && (
          <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-800">
            <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {step === STEPS.CONNECT && (
          <form onSubmit={handleConnect} className="space-y-3">
            <label htmlFor="cp-sending-domain" className="block text-sm font-medium text-gray-700">
              Sending domain
            </label>
            <p className="text-xs text-gray-500">
              Enter the domain that will send Channel Partner email, for example
              {' '}
              <span className="font-mono">theirbrand.com</span>
              . Sending domain is for email From, not login.
            </p>
            <input
              id="cp-sending-domain"
              type="text"
              value={domainInput}
              onChange={(event) => setDomainInput(event.target.value)}
              placeholder="theirbrand.com"
              className="block w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm font-mono shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              autoComplete="off"
              spellCheck={false}
            />
            <button
              type="submit"
              disabled={connecting || !domainInput.trim()}
              className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-500 text-sm font-medium disabled:opacity-60"
            >
              {connecting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />}
              Connect
            </button>
          </form>
        )}

        {step === STEPS.DNS && (
          <div className="space-y-4">
            <div className={`rounded-lg border p-4 flex items-start gap-3 ${
              status === 'failed' ? 'border-red-200 bg-red-50' : 'border-blue-200 bg-blue-50'
            }`}
            >
              {status === 'failed' ? (
                <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              ) : (
                <Loader2 className="h-5 w-5 text-blue-600 animate-spin flex-shrink-0 mt-0.5" />
              )}
              <div>
                <p className={`text-sm font-medium ${status === 'failed' ? 'text-red-900' : 'text-blue-900'}`}>
                  {status === 'failed' ? 'Verification failed' : 'Waiting for DNS'}
                </p>
                <p className={`text-sm mt-1 ${status === 'failed' ? 'text-red-800' : 'text-blue-800'}`}>
                  Add these records at the domain registrar. Status polls every 30 seconds.
                </p>
              </div>
            </div>

            <div className="space-y-3">
              {records.map((record) => (
                <DnsRecordRow key={`${record.type}-${record.hostname}-${record.value}`} record={record} />
              ))}
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={handleVerify}
                disabled={verifying}
                className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-500 text-sm font-medium disabled:opacity-60"
              >
                {verifying ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                Verify
              </button>
              {!showDisconnectConfirm ? (
                <button
                  type="button"
                  onClick={() => setShowDisconnectConfirm(true)}
                  className="inline-flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-red-700"
                >
                  <Trash2 className="h-4 w-4" />
                  Disconnect
                </button>
              ) : (
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={handleDisconnect}
                    disabled={disconnecting}
                    className="text-sm font-medium text-red-700 hover:text-red-800 disabled:opacity-50"
                  >
                    {disconnecting ? 'Removing…' : 'Confirm disconnect'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowDisconnectConfirm(false)}
                    className="text-sm text-gray-500 hover:text-gray-700"
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {step === STEPS.VERIFIED && (
          <div className="rounded-xl border border-green-200 bg-green-50 p-5">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="h-6 w-6 text-green-600 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-green-900">Sending domain verified</h3>
                <p className="text-sm text-green-800 mt-1">
                  From Email is
                  {' '}
                  <span className="font-mono">{payload?.fromEmail}</span>
                  . Merchant invoice From is unchanged.
                </p>
                {!showDisconnectConfirm ? (
                  <button
                    type="button"
                    onClick={() => setShowDisconnectConfirm(true)}
                    className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-red-700 hover:text-red-800"
                  >
                    <Trash2 className="h-4 w-4" />
                    Disconnect / replace
                  </button>
                ) : (
                  <div className="mt-4 flex flex-wrap items-center gap-2">
                    <span className="text-sm text-green-900">Remove the Resend domain and clear this sheet?</span>
                    <button
                      type="button"
                      onClick={handleDisconnect}
                      disabled={disconnecting}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-500 disabled:opacity-50"
                    >
                      {disconnecting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                      Confirm disconnect
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowDisconnectConfirm(false)}
                      disabled={disconnecting}
                      className="text-sm text-gray-600 hover:text-gray-800"
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
